import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import VoiceStatusChip from "@/components/sunovoice/VoiceStatusChip";
import DefaultToggle from "@/components/sunovoice/DefaultToggle";
import { LANGUAGES, SKILLS, clock, deleteSunoVoice, patchSunoVoice } from "@/lib/sunoVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const pill = "inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.05] px-3 py-1.5 text-[12px] font-medium text-white/80 transition-colors hover:bg-white/[0.12]";
const label = (list, k) => (list.find(([key]) => key === k) || [k, k])[1];

/** Status, what Suno learned from, rename and delete. */
export default function SunoVoiceHeader({ voice, onChanged }) {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const rename = async () => {
    const name = window.prompt("Voice name", voice.name);
    if (!name || !name.trim() || name.trim() === voice.name) return;
    try { onChanged(await patchSunoVoice(voice.id, { name: name.trim() })); toast.success("Renamed"); } catch (e) { toast.error("Couldn't rename", { description: safeApiError(e) }); }
  };
  const remove = async () => {
    try { await deleteSunoVoice(voice.id); toast.success("ELPM voice deleted"); navigate("/create/my-voices"); }
    catch (e) { toast.error("Couldn't delete", { description: safeApiError(e) }); }
  };
  return (
    <div data-testid="suno-voice-header" className="flex flex-wrap items-center gap-2">
      <VoiceStatusChip status={voice.status} testId="suno-voice-status" />
      <span data-testid="suno-voice-source-info" className="order-last w-full text-[12px] text-white/50">
        Learned from {voice.source_label || "your recording"} · {clock(voice.start)}–{clock(voice.end)} · {label(SKILLS, voice.skill)} · {label(LANGUAGES, voice.language)}
      </span>
      <span className="ml-auto flex shrink-0 flex-wrap gap-2">
        <DefaultToggle voice={voice} onChanged={onChanged} />
        <button type="button" data-testid="suno-voice-rename" className={pill} onClick={rename}><Pencil className="h-3.5 w-3.5" /> Rename</button>
        <button type="button" data-testid="suno-voice-delete" className={`${pill} hover:border-rose-400/40 hover:text-rose-200`} onClick={() => setConfirm(true)}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
      </span>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-white/10 bg-[#0b0e1c] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{voice.name}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">It disappears from the Model menu. Songs already made with it stay in your library.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="suno-voice-delete-cancel" className="border-white/15 bg-transparent text-white hover:bg-white/10">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="suno-voice-delete-confirm" onClick={remove} className="bg-rose-500 text-white hover:bg-rose-400">Delete voice</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
